import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { Deal, EtapaDeal, TipoDeal } from '../lib/types';

// Cada tablero es una query por `tipo` (una sola llamada, la etapa se agrupa en
// el cliente). El backend ordena por updated_at desc.
export function useDeals(tipo: TipoDeal) {
  return useQuery<Deal[]>({
    queryKey: ['deals', tipo],
    queryFn: async () => (await api.get(`/crm/deals?tipo=${tipo}`)).data.items,
  });
}

export function useSaveDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (d: Partial<Deal> & { deal_id?: string }) => {
      const { deal_id, ...body } = d;
      const res = deal_id
        ? await api.put(`/crm/deals/${deal_id}`, body)
        : await api.post('/crm/deals', body);
      return res.data as Deal;
    },
    onSuccess: (saved) => qc.invalidateQueries({ queryKey: ['deals', saved.tipo] }),
  });
}

/** Mueve de etapa (PATCH). El tipo no cambia, así se invalida su tablero. */
export function useMoveDeal(tipo: TipoDeal) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ dealId, etapa }: { dealId: string; etapa: EtapaDeal }) =>
      (await api.patch(`/crm/deals/${dealId}`, { etapa })).data as Deal,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['deals', tipo] }),
  });
}

/** Cierra una operación de comercialización → crea la comisión. Toca además
 *  viviendas (si se flippea el estado) y comisiones (que C5 leerá). */
export function useCloseDeal() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      dealId: string;
      importe_comision: number;
      estado_cierre: 'venta' | 'alquiler';
      flip_vivienda: boolean;
    }) => {
      const { dealId, ...body } = payload;
      return (await api.post(`/crm/deals/${dealId}/cerrar`, body)).data as {
        deal: Deal;
        commission_id: string;
      };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['deals', 'comercializacion'] });
      qc.invalidateQueries({ queryKey: ['properties'] });
      qc.invalidateQueries({ queryKey: ['commissions'] });
    },
  });
}
